<template>
  <div>
    <!-- Mobile drawer -->
    <v-navigation-drawer app v-model="drawer" dark color="primary darken-4">
      <v-list-item class="py-3">
        <v-list-item-content>
          <v-list-item-title class="font-weight-bold">KANU Nominations</v-list-item-title>
          <v-list-item-subtitle>Administration Portal</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-divider/>
      <v-list dense nav class="mt-1">
        <v-list-item :to="{ name: 'SuperAdmin' }" link exact>
          <v-list-item-icon><v-icon>mdi-home</v-icon></v-list-item-icon>
          <v-list-item-content><v-list-item-title>Dashboard</v-list-item-title></v-list-item-content>
        </v-list-item>
        <v-list-item :to="{ name: 'SuperAdminMembers' }" link>
          <v-list-item-icon><v-icon>mdi-account-group</v-icon></v-list-item-icon>
          <v-list-item-content><v-list-item-title>Membership</v-list-item-title></v-list-item-content>
        </v-list-item>
        <v-list-item :to="{ name: 'SuperAdminNomination' }" link>
          <v-list-item-icon><v-icon>mdi-file-document-edit</v-icon></v-list-item-icon>
          <v-list-item-content><v-list-item-title>Nominations</v-list-item-title></v-list-item-content>
        </v-list-item>

        <v-divider class="my-2"/>
        <!-- Mobile sub-nav (flat list) -->
        <template v-for="item in currentSubNav">
          <v-list-item v-if="!item.children" :key="item.title" :to="{ name: item.to }" link exact>
            <v-list-item-icon><v-icon small>{{ item.icon || 'mdi-chevron-right' }}</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title>{{ item.title }}</v-list-item-title></v-list-item-content>
          </v-list-item>
          <v-list-group v-else :key="item.title + '-group'" :prepend-icon="item.icon || 'mdi-chevron-right'" no-action>
            <template v-slot:activator>
              <v-list-item-content><v-list-item-title>{{ item.title }}</v-list-item-title></v-list-item-content>
            </template>
            <v-list-item v-for="child in item.children" :key="child.title" :to="{ name: child.to }" link>
              <v-list-item-content><v-list-item-title>{{ child.title }}</v-list-item-title></v-list-item-content>
            </v-list-item>
          </v-list-group>
        </template>
      </v-list>
      <template v-slot:append>
        <v-divider/>
        <v-list dense nav>
          <v-list-item @click="logout">
            <v-list-item-icon><v-icon color="red lighten-2">mdi-logout</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title class="red--text text--lighten-2">Logout</v-list-item-title></v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Main app bar -->
    <v-app-bar color="primary darken-4" dark app elevate-on-scroll
               :extension-height="currentSubNav.length ? 40 : 0">
      <v-app-bar-nav-icon class="hidden-md-and-up" @click="drawer = !drawer"/>
      <v-toolbar-title class="font-weight-bold subtitle-1">KANU Nominations</v-toolbar-title>
      <v-spacer/>
      <!-- Top-level section nav (desktop) -->
      <div class="hidden-sm-and-down">
        <v-btn text small exact :to="{ name: 'SuperAdmin' }">
          <v-icon left small>mdi-home</v-icon>Dashboard
        </v-btn>
        <v-btn text small :to="{ name: 'SuperAdminMembers' }">
          <v-icon left small>mdi-account-group</v-icon>Membership
        </v-btn>
        <v-btn text small :to="{ name: 'SuperAdminNomination' }">
          <v-icon left small>mdi-file-document-edit</v-icon>Nominations
        </v-btn>
        <v-btn text small @click="logout">
          <v-icon left small>mdi-logout</v-icon>Logout
        </v-btn>
      </div>

      <!-- Section sub-nav in extension slot -->
      <template v-slot:extension v-if="currentSubNav.length">
        <div class="sub-nav-strip hidden-sm-and-down">
          <template v-for="item in currentSubNav">
            <!-- Simple link -->
            <v-btn v-if="!item.children" :key="item.title"
                   text x-small exact :to="{ name: item.to }"
                   class="sub-nav-btn">
              {{ item.title }}
            </v-btn>
            <!-- Dropdown -->
            <v-menu v-else :key="item.title" bottom offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn text x-small dark v-bind="attrs" v-on="on" class="sub-nav-btn">
                  {{ item.title }}<v-icon right x-small>mdi-menu-down</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item v-for="child in item.children" :key="child.title"
                             :to="{ name: child.to }" link exact>
                  <v-list-item-title class="body-2">{{ child.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </div>
      </template>
    </v-app-bar>
  </div>
</template>

<script>
const HOME_NAV = [
  { title: 'Overview',     to: 'SuperAdmin',             icon: 'mdi-view-dashboard' },
  { title: 'Analytics',   to: 'SuperAdminHomeAnalytics', icon: 'mdi-chart-line' },
];

const MEMBERSHIP_NAV = [
  { title: 'Overview', to: 'SuperAdminMembers', icon: 'mdi-home' },
  {
    title: 'Members', icon: 'mdi-account-multiple',
    children: [
      { title: 'Member Levels',       to: 'AdminMemberLevels' },
      { title: 'Full Members',        to: 'AdminMember' },
      { title: 'Temporary Members',   to: 'AdminTempMember' },
      { title: 'Archived Members',    to: 'AdminTempStoreMember' },
      { title: 'Failed USSD',         to: 'FailedUSSD' },
    ],
  },
  {
    title: 'Summary', icon: 'mdi-chart-bar',
    children: [
      { title: 'Full Members by County',      to: 'AdminMemberSummary' },
      { title: 'Full Members by Level',       to: 'AdminMemberLevelSummary' },
      { title: 'Temp Members by County',      to: 'AdminTempMemberSummary' },
      { title: 'Temp Members by Level',       to: 'AdminTempMemberSummary' },
    ],
  },
  {
    title: 'Payments', icon: 'mdi-cash-multiple',
    children: [
      { title: 'All Payments',        to: 'AdminPayments' },
      { title: 'Accept Bulk Payments',to: 'BulkPayment' },
      { title: 'Verify Payments',     to: 'CheckPayment' },
      { title: 'Unaccounted Payments',to: 'UnaccountedPayments' },
    ],
  },
  {
    title: 'Reports', icon: 'mdi-file-chart',
    children: [
      { title: 'Member Reports',  to: 'AdminMemberReports' },
      { title: 'Payment Reports', to: 'AdminPaymentReports' },
    ],
  },
  {
    title: 'Utilities', icon: 'mdi-tools',
    children: [
      { title: 'Send Bulk SMS',     to: 'AdminMemberSummary' },
      { title: 'Verify Membership', to: 'AdminTempMemberSummary' },
    ],
  },
];

const NOMINATION_NAV = [
  { title: 'Overview', to: 'SuperAdminNomination', icon: 'mdi-home' },
  {
    title: 'Open Posts', icon: 'mdi-door-open',
    children: [
      { title: 'Internal Posts', to: 'SuperAdminInternalNominationPosts' },
      { title: 'Political Posts', to: 'SuperAdminNominationPosts' },
    ],
  },
  {
    title: 'Nominees', icon: 'mdi-account-check',
    children: [
      { title: 'All Nominees',                    to: 'SuperAdminNominationNominees' },
      { title: 'Nominees by County',              to: 'SuperAdminNominationNominees' },
      { title: 'Nominees by Seat',                to: 'SuperAdminNominationNominees' },
      { title: 'Nominees by Constituency',        to: 'SuperAdminNominationNominees' },
      { title: 'Nominees by Ward',                to: 'SuperAdminNominationNominees' },
    ],
  },
  {
    title: 'Payments', icon: 'mdi-cash',
    children: [
      { title: 'All Payments',         to: 'SuperAdminNominationPayments' },
      { title: 'Bulk SMS Payments',    to: 'SuperAdminNominationPayments' },
      { title: 'Campaign Payments',    to: 'SuperAdminNominationPayments' },
    ],
  },
  {
    title: 'Results', icon: 'mdi-chart-bar',
    children: [
      { title: 'Internal Results', to: 'SuperAdminNominationResults' },
      { title: 'Political Results', to: 'SuperAdminNominationResults' },
    ],
  },
  {
    title: 'Utilities', icon: 'mdi-tools',
    children: [
      { title: 'Send Bulk SMS',     to: 'AdminMemberSummary' },
      { title: 'Verify Membership', to: 'AdminTempMemberSummary' },
    ],
  },
];

export default {
  name: "SuperAdminNavigation",
  data() {
    return { drawer: false };
  },
  computed: {
    currentSubNav() {
      const path = this.$route.path;
      if (path.startsWith('/super-admin/nomination')) return NOMINATION_NAV;
      if (path.startsWith('/super-admin/membership'))  return MEMBERSHIP_NAV;
      return HOME_NAV;
    },
  },
  methods: {
    logout() {
      swal.fire({
        title: 'Log out?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#c62828',
        cancelButtonColor: '#757575',
        confirmButtonText: 'Yes, log me out!',
      }).then(result => {
        if (result.value) {
          localStorage.removeItem('token');
          localStorage.removeItem('PARTYSYSTEM');
          this.$store.dispatch('logout_user');
          toast.fire('Logged out!', '', 'success');
          this.$router.push('/login');
        }
      });
    },
  },
};
</script>

<style scoped>
.sub-nav-strip {
  display: flex;
  align-items: center;
  height: 40px;
  width: 100%;
  background: rgba(0,0,0,0.15);
  padding: 0 12px;
}
.sub-nav-btn {
  letter-spacing: .04em;
  font-size: 0.75rem !important;
  height: 32px !important;
}
</style>
